import { EntityNode } from "./EntityNode";
import { RotationNode } from "../Nodes/RotationNode";
import { ResizeNode } from "../Nodes/ResizeNode";
import { DimensionNode } from "../Nodes/DimensionNode";
import { GameObjectManager } from "../GameObjectManager";
import { CocosConstants } from "../../../../constants";
import { ActionStateEnum } from "../../state/actionState/ActionState";
import { Utils } from "../../utils/Utils";
import { PlanManager } from "../../../../cocosPlan";
import { Events, eventsManager, Joinery, math, SceneConstants, TechnicalElement } from "@rhinov/savane-js";

declare var cc: any;

export class TechnicalElementNode extends EntityNode {

    static HALF_RESIZE_CURSOR_SIZE = 5;
    override savaneEntity: TechnicalElement;

    constructor(techElement: TechnicalElement) {
        super(techElement);
        this.savaneEntity = techElement;
        this._sprite = new cc.Sprite();
        this.updateSprite(techElement, '');
        this._isValid = true;
        this.rotateMenu = new RotationNode(techElement);
        this.addChild(this.rotateMenu);
        if (this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.waterSource &&
            this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketSixteen &&
            this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketThirtyTwo &&
            this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketTV &&
            this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.spotLight) {
            this.resizeMenu = new ResizeNode(this.savaneEntity, undefined as any, undefined as any);
            this.addChild(this.resizeMenu, 8);
        }
        this.addChild(this._sprite);

        this.createDimensions();

        this.needRedraw = true;

        this.update(0);

        this.setTechnicalElementTransparency = eventsManager.instance.addListener(Events.WALL_SET_TRANSPARENCY, (_event: any) => {
            this.needRedraw = true;
            this.transparentDraw = PlanManager.getInstance()._wallWireframe;
        });

        this.hideTechnicalElementAxo = eventsManager.instance.addListener(Events.HIDE_AXO, (_event: any) => {
            this.needRedraw = true;
            if (this.savaneEntity.objectId === SceneConstants.TechnicalElementType.rosette ||
                this.savaneEntity.objectId === SceneConstants.TechnicalElementType.beam ||
                this.savaneEntity.objectId === SceneConstants.TechnicalElementType.guardrail ||
                this.savaneEntity.objectId === SceneConstants.TechnicalElementType.frame ||
                this.savaneEntity.objectId === SceneConstants.TechnicalElementType.spotLight ||
                this.savaneEntity.objectId === SceneConstants.TechnicalElementType.airConditioner) {
                this.setVisible(!PlanManager.getInstance()._hideAxo);
            }
        });
    }

    updateDimensionNode(node: any, begin: any, end: any, normal: any, dist: number) {
        var cote_begin = new cc.p(begin.x + dist * normal.x, begin.y + dist * normal.y);
        var cote_end = new cc.p(end.x + dist * normal.x, end.y + dist * normal.y);
        var cote_pos = new cc.p((cote_begin.x + cote_end.x) / 2, (cote_begin.y + cote_end.y) / 2);
        //var middleP = new cc.p(begin.x + (end.x-begin.x)*0.5,begin.y + (end.y-begin.y)*0.5);
        node.setPosition(cote_pos);
        var calcDist = Math.sqrt((end.x - begin.x) * (end.x - begin.x) + (end.y - begin.y) * (end.y - begin.y));
        node.setDimensionValue(calcDist, PlanManager.getInstance().getZoomScale());

        var angle = Math.atan2(end.x - begin.x, end.y - begin.y);
        var cocosAngle = (angle + Math.PI * 0.5) * 180 / Math.PI;
        if (cocosAngle < -90.0)
            cocosAngle += 180;
        if (cocosAngle >= 90.0)
            cocosAngle -= 180;

        var parentAngle = (this.savaneEntity.angle * 180) / Math.PI;
        if (parentAngle < -90.0)
            cocosAngle += 180;
        if (parentAngle > 90.0)
            cocosAngle -= 180;

        node.setRotation(cocosAngle);
    }

    updateDimensions() {
        var norm1 = new cc.p(0, -1);
        this.updateDimensionNode(this.dim1, new cc.p((this.savaneEntity.realLength / 2) / 10, -(this.savaneEntity.realWidth / 2) / 10), new cc.p(-(this.savaneEntity.realLength / 2) / 10, -(this.savaneEntity.realWidth / 2) / 10), norm1, Utils.DIMENSION_DIST * 1.5);

        var norm2 = new cc.p(1, 0);
        this.updateDimensionNode(this.dim2, new cc.p((this.savaneEntity.realLength / 2) / 10, -(this.savaneEntity.realWidth / 2) / 10), new cc.p((this.savaneEntity.realLength / 2) / 10, (this.savaneEntity.width / 2) / 10), norm2, Utils.DIMENSION_DIST * 1.5);
    }

    createDimensions() {
        this.dim1 = new DimensionNode();
        this.dim1.setName('techElementDimension1');
        this.dim1.setTag(this.savaneEntity.id);
        this.dim1.setVisible(false);
        this.addChild(this.dim1, 8);

        this.dim2 = new DimensionNode();
        this.dim1.setName('techElementDimension2');
        this.dim2.setTag(this.savaneEntity.id);
        this.dim2.setVisible(false);
        this.addChild(this.dim2, 8);
    }

    setValidity(value: boolean) {
        if (this._isValid !== value) {
            this.needRedraw = true;
        }

        this._isValid = value;
    }
    /**
    * Select node and children
    **/
    setSelected(value: boolean) {
        if (this._isSelected !== value) {
            this.needRedraw = true;
        }

        super.setSelected(value);
    }

    /**
    * Hover node
    **/
    setHovered(value: boolean) {
        if (this._isHovered !== value) {
            this.needRedraw = true;
        }

        super.setHovered(value);
    }

    updateSprite(techElement: TechnicalElement, wording: string) {
        switch (techElement.objectId) {
            case SceneConstants.TechnicalElementType.socketSixteen:
                this._sprite.initWithSpriteFrameName('plan/socket16A' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.socketThirtyTwo:
                this._sprite.initWithSpriteFrameName('plan/socket32A' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.socketTV:
                this._sprite.initWithSpriteFrameName('plan/socketTV' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.switchBoard:
                this._sprite.initWithSpriteFrameName('plan/switchBoard' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.wardRobe:
                this._sprite.initWithSpriteFrameName('plan/wardRobe' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.spotLight:
                this._sprite.initWithSpriteFrameName('plan/spotLight' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.rosette:
                this._sprite.initWithSpriteFrameName('plan/rosette' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.beam:
            case SceneConstants.TechnicalElementType.frame:
            case SceneConstants.TechnicalElementType.guardrail:
            case SceneConstants.TechnicalElementType.wallDecoration:
                this._sprite.initWithSpriteFrameName('plan/beam' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.waterSource:
            case SceneConstants.TechnicalElementType.pole:
            case SceneConstants.TechnicalElementType.serviceShaft:
                if (techElement.shapeType !== SceneConstants.ShapeType.ellipse) {
                    this._sprite.initWithSpriteFrameName('plan/poleChip' + wording + '.png');
                } else {
                    this._sprite.initWithSpriteFrameName('plan/poleCircleChip' + wording + '.png');
                }
                if (techElement.objectId === SceneConstants.TechnicalElementType.waterSource) {
                    this._sprite.removeAllChildren();
                    var superSprite = new cc.Sprite();
                    superSprite.setTexture(CocosConstants.resourcesBasePath + '/resources/water.png');
                    superSprite.setScale(0.75);
                    this._sprite.addChild(superSprite);
                    superSprite.setPosition(cc.p(this._sprite.width * 0.5, this._sprite.height * 0.5));
                }
                break;
            case SceneConstants.TechnicalElementType.stove:
                this._sprite.initWithSpriteFrameName('plan/stoveChip' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.fireplace:
                this._sprite.initWithSpriteFrameName('plan/fireplaceChip' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.radiator:
                this._sprite.initWithSpriteFrameName('plan/radiatorChip' + wording + '.png');
                this._sprite.flippedX = true;
                break;

            case SceneConstants.TechnicalElementType.airConditioner:
                this._sprite.initWithSpriteFrameName('plan/airconChip' + wording + '.png');
                break;

            case SceneConstants.TechnicalElementType.boiler:
                this._sprite.initWithSpriteFrameName('plan/boilerChip' + wording + '.png');
                break;

            default:
                //FIXME : ceiling box is no sprite and plan/arrow.png is undefined
                // this._sprite.initWithSpriteFrameName('plan/arrow.png');
                break;

        }
        this._sprite.setScale(techElement.realLength / this._sprite.width / 10, techElement.realWidth / this._sprite.height / 10);
    }

    updateModel(techElement: TechnicalElement/*,isValid*/) {
        var planManager = PlanManager.getInstance();

        /*        if (isValid != undefined) {
                    this._isValid = isValid;
                }*/

        // In decoration state, always hide rotation and resize nodes
        if (planManager.getAction() !== ActionStateEnum.idleDecoration) {
            this.rotateMenu.setVisible(this._isSelected);
            if (this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.waterSource && this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketSixteen &&
                this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketThirtyTwo && this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketTV &&
                this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.spotLight) {
                this.resizeMenu.setVisible(this._isSelected);
                this.resizeMenu.update();
            }
            this.rotateMenu.update();
        }
        else {
            this.rotateMenu.setVisible(false);
            if (this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.waterSource && this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketSixteen &&
                this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketThirtyTwo && this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.socketTV &&
                this.savaneEntity.objectId !== SceneConstants.TechnicalElementType.spotLight) {
                this.resizeMenu.setVisible(false);
            }
        }

        this.updateDimensions();

        this.dim1.setVisible(this._isSelected);
        this.dim1.update();
        this.dim2.setVisible(this._isSelected)
        this.dim2.update();

        var wording = this._isValid ? (this._isSelected ? 'Selected' : (this._isHovered ? 'Hovered' : '')) : 'Invalid';

        if (techElement.objectId !== SceneConstants.TechnicalElementType.ceilingBox) {
            this.updateSprite(techElement, wording);
        } else {
            this.updateNoSprite(this._isValid, this._isSelected);
        }
    }

    toResize(pos: math.vec3): boolean {
        if (this.savaneEntity.objectId === SceneConstants.TechnicalElementType.waterSource ||
            this.savaneEntity.objectId === SceneConstants.TechnicalElementType.socketSixteen ||
            this.savaneEntity.objectId === SceneConstants.TechnicalElementType.socketThirtyTwo ||
            this.savaneEntity.objectId === SceneConstants.TechnicalElementType.socketTV ||
            this.savaneEntity.objectId === SceneConstants.TechnicalElementType.spotLight) {
            return false;
        } else {
            return this.resizeMenu.toResize(pos);
        }
    }
    toRotate(pos: math.vec3) {
        if (this.savaneEntity.objectId === SceneConstants.TechnicalElementType.spotLight) {
            return (false);
        }
        return this.rotateMenu.toRotate(pos);
    }

    update(dt: number) {
        if (this.needRedraw || this._isSelected || this._isHovered || !this._isValid) {
            super.update(dt);

            var cocosPosition = GameObjectManager.convertToCocosCoord(this.savaneEntity.position);
            this.setPosition(cocosPosition);
            this.setRotation(-this.savaneEntity.angle * 180.0 / Math.PI);

            this.updateModel(this.savaneEntity);
            this.needRedraw = false;

            if (this.transparentDraw) {
                this._sprite.setOpacity(64);
            }
            else {
                this._sprite.setOpacity(255);
            }
        }
    }

    updateNoSprite(isValid:boolean, isSelected: boolean) {
        this.clear();
        var h = this.savaneEntity.realWidth * 0.1;
        var w = this.savaneEntity.realLength * 0.1;
        var x = -w * 0.5;
        var y = -h;

        var color = this.getOverLapColor(isValid, isSelected);
        color.plain.a = 164;

        var boderSize = 1;
        if (boderSize * PlanManager.getInstance().getZoomScale() < 1) {
            boderSize /= PlanManager.getInstance().getZoomScale();
        }

        var angle = 0;
        var sgn = 1;
        if ((this.savaneEntity.parent as Joinery).wall) {
            angle = Math.atan2((this.savaneEntity.parent as Joinery).wall.wallDirection[0], (this.savaneEntity.parent as Joinery).wall.wallDirection[1]);
            if (!(this.savaneEntity.parent as Joinery).orientation) {
                angle += Math.PI;
                sgn = -sgn;
            }
        }
        if (this.savaneEntity.transform.localPosition[1] > 0) { sgn = -sgn; }

        angle = (angle - Math.PI * 0.5) * 180 / Math.PI;
        this.setRotation(angle);
        this.drawRect(new cc.p(x + 8, sgn * y), new cc.p(x + w - 8, sgn * (y + h)), color.plain, boderSize, color.border);
    }

    getOverLapColor(isValid: boolean, isSelected: boolean) {
        var colorItem = (isValid ? (isSelected ? {
            plain: new cc.color('#fbe800'),
            border: new cc.color('#fbe800')
        } : {
                plain: new cc.color(255, 255, 255),
                border: new cc.Color(66, 80, 94, 255)
            }) : { plain: this._NOT_VALID_OVERLAP_COLOR, border: this._NOT_VALID_OVERLAP_COLOR });
        if (PlanManager.getInstance().highlightMode) {
            colorItem.plain.a = 75;
        }
        if (this.transparentDraw) {
            colorItem.plain.a = 64;
        }
        return colorItem;
    }

    dispose() {
    }

    removeListeners() {
        eventsManager.instance.removeListener(this.setTechnicalElementTransparency);
        eventsManager.instance.removeListener(this.hideTechnicalElementAxo);
    }

    onExit() {
        super.onExit();
        this.removeListeners();
    }
}
